import axios from "axios";
import { getAuth, signOut } from "firebase/auth";
import { firebaseApp } from "../utilities/firebase";

export const BASE_URL =
  process.env.NODE_ENV === "development"
    ? "http://localhost:8080/"
    : process.env.REACT_APP_API_URL || "https://sound-api-9b72fb056bd1.herokuapp.com/";

export const client = axios.create({
  baseURL: BASE_URL,
});

let refreshPromise = null;

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalConfig = error.config;

    if (error.response?.status !== 401 || originalConfig._retry) {
      return Promise.reject(error);
    }

    originalConfig._retry = true;

    const fbAuth = getAuth(firebaseApp);
    const errorMessage = error.response.data?.error;

    // SOUND-715. "User not found" is NOT an auth failure and must not sign
    // anyone out. It means the token is perfectly valid and there is simply no
    // user document yet — which is exactly what a half-finished signup looks
    // like now that provisioning waits for a confirmed email address.
    //
    // This blanket sign-out was the real cause of two DEV failures that looked
    // like bugs in the new flow. `useUser` polls GET /users/me in the
    // background as soon as onAuthStateChanged fires, so within moments of
    // signing up the app fetched /users/me, got this 401, and signed the person
    // out — while they were still reading "confirm your email". The panel then
    // reported "not confirmed yet" (a null currentUser reads as unverified) and
    // later "Not signed in". Neither was the real story.
    //
    // Rejecting without signing out hands the decision back to the caller,
    // which is the only place that knows whether this is a signup to resume or
    // a genuinely orphaned session.
    if (errorMessage === "User not found") {
      return Promise.reject(error);
    }

    // Only refresh on expired tokens — all other 401s are real auth failures
    if (errorMessage !== "Invalid or expired token") {
      await signOut(fbAuth).catch(() => {});
      return Promise.reject(error);
    }

    const currentUser = fbAuth.currentUser;
    if (!currentUser) {
      await signOut(fbAuth).catch(() => {});
      return Promise.reject(error);
    }

    try {
      if (!refreshPromise) {
        refreshPromise = currentUser.getIdToken(true).finally(() => {
          refreshPromise = null;
        });
      }

      const newToken = await refreshPromise;
      const userId = originalConfig.headers.Authorization?.split(".")[0];
      originalConfig.headers.Authorization = `${userId}.${newToken}`;
      return client(originalConfig);
    } catch {
      await signOut(fbAuth).catch(() => {});
      return Promise.reject(error);
    }
  }
);
